import { PlotMouseEvent } from "plotly.js";
import React, { useLayoutEffect, useRef, useState } from "react";
import moment from "moment";
import { Col, Row, Space } from "antd";
import { COREBody } from "../../Typography/COREBody";
import { coreTooltipStyle } from "../../Overlay/CORETooltip";
import {
  formatCurrencyValue,
  formatEnergyMWh,
  formatNumericValue,
  formatPowerMW,
} from "../../../shared/unitFormatting";
import {
  getSymbolFromCurrency,
  CurrencyName,
} from "../../../shared/CurrencySymbol";
import { generateTestId } from "../../../shared/testids/testids";

// Min/max width used for tooltip sizing and viewport boundary calculations
const TOOLTIP_MIN_WIDTH = 300;
const TOOLTIP_MAX_WIDTH = 400;
const TOOLTIP_OFFSET = 16;
// Small upward nudge so the tooltip header lines up with the cursor.
const TOOLTIP_VERTICAL_NUDGE = 10;

/**
 * Positions the tooltip beside the cursor using its *measured* size: to the
 * right when it fits, flipped to just left of the cursor otherwise, and
 * clamped to the viewport on both axes so it never renders off-screen.
 * Exported for unit tests.
 */
export const computeTooltipPosition = (
  cursor: { x: number; y: number },
  size: { width: number; height: number },
  viewport: { width: number; height: number }
): { left: number; top: number } => {
  const fitsRight = cursor.x + TOOLTIP_OFFSET + size.width <= viewport.width;
  const left = fitsRight
    ? cursor.x + TOOLTIP_OFFSET
    : Math.max(TOOLTIP_OFFSET, cursor.x - size.width - TOOLTIP_OFFSET);
  const top = Math.max(
    TOOLTIP_OFFSET,
    Math.min(
      cursor.y - TOOLTIP_VERTICAL_NUDGE,
      viewport.height - size.height - TOOLTIP_OFFSET
    )
  );
  return { left, top };
};

// Union of all trace types we need to visually distinguish in the legend icon.
type TraceIconVariant = "bar" | "area" | "line" | "dashedLine";

// Normalised per-trace data that the tooltip rows are built from.
type TooltipPoint = {
  name: string;
  value: number | null;
  unit: string | null;
  color: string;
  variant: TraceIconVariant;
};

/** Viewport-space rectangle of a hovered mark. */
export type ChartPointBBox = {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
};

export type COREChartTooltipState = {
  points: PlotMouseEvent["points"];
  /** Cursor position at the moment plotly reported the hover. */
  position: { x: number; y: number };
  /**
   * Bounds of the hovered mark, in viewport coordinates.
   *
   * Lets a tooltip anchor itself to the mark (e.g. centred above a bar) rather
   * than to the cursor. Absent when plotly does not report a bounding box for
   * the hovered point — fall back to `position`.
   */
  pointBBox?: ChartPointBBox;
};

type RawTraceData = Record<string, unknown>;
const asRaw = (data: unknown): RawTraceData => data as RawTraceData;

const ENERGY_UNIT_REGEX = /^(?:[kMGT]?Wh)$/;
const POWER_UNIT_REGEX = /^(?:[kMGT]?W)$/;
const DIRECT_UNIT_FORMATTERS: Record<string, (value: number) => string> = {
  MWh: formatEnergyMWh,
  MW: formatPowerMW,
};

export const stripHtml = (text: string): string =>
  text.replace(/<[^>]{0,500}>/g, "").trim();

export const extractUnitFromHoverTemplate = (
  hovertemplate: unknown
): string | null => {
  if (typeof hovertemplate !== "string") return null;

  const cleanTemplate = hovertemplate.replace(/<extra>[\s\S]*?<\/extra>/gi, "");
  const unitMatch = cleanTemplate.match(/%\{y[^}]*\}\s*([^<\n\r]*)/i);
  const unit = unitMatch?.[1]?.trim();
  if (!unit || unit.includes("%{")) return null;
  return stripHtml(unit) || null;
};

export const extractUnitFromAxisTitle = (axisTitle: unknown): string | null => {
  const titleText =
    typeof axisTitle === "string"
      ? axisTitle
      : axisTitle !== null &&
        axisTitle !== undefined &&
        typeof asRaw(axisTitle).text === "string"
      ? (asRaw(axisTitle).text as string)
      : null;

  if (!titleText) return null;
  const cleanTitle = stripHtml(titleText);
  const bracketUnitMatch = cleanTitle.match(/\(([^)]{1,200})\)\s{0,50}$/);
  if (bracketUnitMatch?.[1]?.trim()) return bracketUnitMatch[1].trim();

  return cleanTitle.trim();
};

const extractYUnit = (
  point: PlotMouseEvent["points"][number]
): string | null => {
  const data = asRaw(point.data);

  const unitFromHoverTemplate = extractUnitFromHoverTemplate(
    data.hovertemplate
  );
  if (unitFromHoverTemplate) return unitFromHoverTemplate;

  const pointRaw = asRaw(point);
  const yAxis = pointRaw.yaxis ? asRaw(pointRaw.yaxis) : null;
  if (typeof yAxis?.ticksuffix === "string" && yAxis.ticksuffix.trim()) {
    return yAxis.ticksuffix.trim();
  }

  if (typeof yAxis?.tickprefix === "string" && yAxis.tickprefix.trim()) {
    return yAxis.tickprefix.trim();
  }

  return extractUnitFromAxisTitle(yAxis?.title);
};

export const parseCurrencyUnit = (
  unit: string
): { symbol: string; suffix: string } | null => {
  const compact = unit.replace(/\s+/g, "");
  const symbolMatch = compact.match(/^([$€£¥฿])(.*)$/);
  if (symbolMatch) {
    return {
      symbol: symbolMatch[1],
      suffix: symbolMatch[2] ?? "",
    };
  }

  const codeMatch = compact.match(/^([A-Za-z]{3})(.*)$/);
  if (!codeMatch) return null;

  const code = codeMatch[1].toUpperCase() as CurrencyName;
  const symbol = getSymbolFromCurrency(code);
  if (!symbol) return null;

  return {
    symbol,
    suffix: codeMatch[2] ?? "",
  };
};

export const isEnergyUnit = (unit: string): boolean =>
  ENERGY_UNIT_REGEX.test(unit);

export const isPowerUnit = (unit: string): boolean =>
  POWER_UNIT_REGEX.test(unit);

const isEnergyOrPowerUnit = (unit: string): boolean =>
  isEnergyUnit(unit) || isPowerUnit(unit);

const formatWithUnit = (value: number, unit: string): string =>
  `${formatNumericValue(value)} ${unit}`;

const formatEnergyOrPowerUnit = (value: number, unit: string): string => {
  const directFormatter = DIRECT_UNIT_FORMATTERS[unit];
  if (directFormatter) return directFormatter(value);
  return formatWithUnit(value, unit);
};

const tryFormatCurrencyUnit = (value: number, unit: string): string | null => {
  const currency = parseCurrencyUnit(unit);
  if (!currency) return null;

  const formattedValue = formatCurrencyValue(value, currency.symbol);

  if (currency.suffix.startsWith("/")) {
    // Rate unit like $/MWh → "$5.90 / MWh" or "-$5.90 / MWh"
    const denominatorPart = currency.suffix.slice(1);
    return `${formattedValue} / ${denominatorPart}`;
  }

  // Pure currency → "$33.45" or "-$233.69"
  return `${formattedValue}`;
};

export const formatTooltipValue = (
  value: number | null,
  unit: string | null
): string => {
  if (value === null || value === undefined) return "-";
  if (!unit) return formatNumericValue(value);

  const trimmedUnit = unit.trim();
  if (!trimmedUnit) return formatNumericValue(value);

  const currencyFormatted = tryFormatCurrencyUnit(value, trimmedUnit);
  if (currencyFormatted) return currencyFormatted;

  if (isEnergyOrPowerUnit(trimmedUnit))
    return formatEnergyOrPowerUnit(value, trimmedUnit);

  return formatWithUnit(value, trimmedUnit);
};

/**
 * Determines the representative colour for a single hovered Plotly point.
 *
 * Priority order:
 * 1. `fillcolor` – set on area / stacked-scatter traces.
 * 2. `marker.color` – a string or the first element of an array.
 * 3. Falls back to `"#888"` when no colour information is available.
 */
const extractColor = (point: PlotMouseEvent["points"][number]): string => {
  const data = asRaw(point.data);
  // fillcolor is set on area (stacked scatter) traces
  if (typeof data.fillcolor === "string") return data.fillcolor;

  const marker = data.marker ? asRaw(data.marker) : null;
  const markerColor = marker?.color;
  if (typeof markerColor === "string") return markerColor;
  if (Array.isArray(markerColor) && markerColor.length > 0)
    return markerColor[0] as string;

  return "#888";
};

/**
 * Maps a hovered Plotly point to one of four visual legend-icon variants:
 * - `"bar"`        – filled rectangle for bar-chart traces.
 * - `"area"`       – filled rectangle for area / stacked-scatter traces that
 *                    carry a `fill` property other than `"none"`.
 * - `"dashedLine"` – dashed SVG line when `line.dash` is not `"solid"`.
 * - `"line"`       – solid SVG line for all other scatter-like traces.
 */
const extractVariant = (
  point: PlotMouseEvent["points"][number]
): TraceIconVariant => {
  const data = asRaw(point.data);
  if (data.type === "bar") return "bar";
  if (data.fill && data.fill !== "none") return "area";
  const dash = asRaw(data.line)?.dash;
  if (typeof dash === "string" && dash !== "solid") return "dashedLine";
  return "line";
};

/**
 * Converts the raw Plotly `points` array emitted by a `PlotMouseEvent` into
 * the simplified {@link TooltipPoint} shape used by the tooltip rows.
 *
 * Points whose `y` value is `null` or `undefined` are filtered out so that
 * traces with no data at the hovered x-position are not shown.
 */
const buildTooltipPoints = (
  rawPoints: PlotMouseEvent["points"]
): TooltipPoint[] =>
  [...rawPoints]
    .sort((a, b) => a.curveNumber - b.curveNumber)
    .filter((p) => p.y !== null && p.y !== undefined)
    .map((p) => ({
      name: String(asRaw(p.data).tooltipLabel ?? asRaw(p.data).name ?? ""),
      value: typeof p.y === "number" ? p.y : null,
      unit: extractYUnit(p),
      color: extractColor(p),
      variant: extractVariant(p),
    }));

/**
 * Small legend icon rendered to the left of each trace name inside the
 * tooltip. Renders a filled rectangle for `"bar"` and `"area"` variants, or
 * a solid / dashed horizontal line for `"line"` and `"dashedLine"` variants.
 */
const TraceIcon = React.memo(function TraceIcon({
  variant,
  color,
}: {
  variant: TraceIconVariant;
  color: string;
}) {
  if (variant === "bar" || variant === "area") {
    return (
      <span
        style={{
          display: "block",
          width: 12,
          height: 12,
          backgroundColor: color,
          borderRadius: 2,
        }}
      />
    );
  }

  // Solid or dashed line icon — use SVG so the dash pattern is identical
  // across Chrome and Firefox (border-top dashed renders differently per browser).
  return (
    <svg
      width={20}
      height={4}
      style={{
        display: "block",
      }}
      aria-hidden="true"
    >
      <line
        x1={0}
        y1={2}
        x2={20}
        y2={2}
        stroke={color}
        strokeWidth={2}
        strokeDasharray={variant === "dashedLine" ? "3 2" : undefined}
      />
    </svg>
  );
});

export type CORETimeSeriesChartTooltipProps = COREChartTooltipState;

/**
 * Converts a Plotly/strftime format string (e.g. "%H:%M") to a moment.js
 * format string (e.g. "HH:mm") so we can format the x-axis date value
 * using the same hoverformat defined in the chart layout.
 */
const strftimeToMomentFormat = (strftime: string): string =>
  strftime
    .replace(/%Y/g, "YYYY")
    .replace(/%m/g, "MM")
    .replace(/%d/g, "DD")
    .replace(/%H/g, "HH")
    .replace(/%M/g, "mm")
    .replace(/%S/g, "ss")
    .replace(/%b/g, "MMM")
    .replace(/%B/g, "MMMM")
    .replace(/%y/g, "YY");

/**
 * Default floating tooltip for time-series / XY charts.
 * Header = x-axis value (formatted as date/time when applicable).
 * Rows = one per trace showing trace name + y value with unit.
 *
 * Pass this to `COREBasicPanelChart` (or any COREChart-based component) via
 * the `customTooltip` render prop:
 * ```tsx
 * customTooltip={renderTimeSeriesChartTooltip}
 * ```
 */
export const CORETimeSeriesChartTooltip: React.FC<
  CORETimeSeriesChartTooltipProps
> = ({ points, position }) => {
  // Measure the rendered tooltip so positioning uses its actual size, not the
  // max-width worst case (which detached the tooltip from the cursor and let
  // it overflow the viewport in narrow layouts like the map sider).
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null
  );
  // Re-measure whenever the hovered points change (each hover yields a fresh
  // array), since a different row count/label changes the rendered size. The
  // guarded setSize returns the previous object when unchanged, so React bails
  // out and this cannot loop.
  useLayoutEffect(() => {
    const el = tooltipRef.current;
    if (!el || !points || points.length === 0) return;
    const { offsetWidth: width, offsetHeight: height } = el;
    setSize((prev) =>
      prev?.width === width && prev?.height === height
        ? prev
        : { width, height }
    );
  }, [points]);

  if (!points || points.length === 0) return null;

  // Use the x-value of the first point as the shared date/time header.
  const xValue = points[0].x;
  const hoverformat = points[0].xaxis.hoverformat;
  const xAxisDataType = points[0].xaxis.type;
  const dateLabel =
    xValue != null
      ? xAxisDataType === "date" && hoverformat
        ? moment(String(xValue)).format(strftimeToMomentFormat(hoverformat))
        : String(xValue)
      : "";

  // Discard points with no y-value; if nothing remains, render nothing.
  const tooltipPoints = buildTooltipPoints(points);
  if (tooltipPoints.length === 0) return null;

  const { left, top } = computeTooltipPosition(
    position,
    // Until the first measurement lands (one frame), estimate with the min
    // width — closer to the common case than the max.
    size ?? { width: TOOLTIP_MIN_WIDTH, height: 0 },
    { width: window.innerWidth, height: window.innerHeight }
  );

  return (
    <div
      ref={tooltipRef}
      className="core-chart-tooltip-layer core-chart-floating-tooltip"
      style={{
        ...coreTooltipStyle,
        left,
        top,
        minWidth: TOOLTIP_MIN_WIDTH,
        maxWidth: TOOLTIP_MAX_WIDTH,
      }}
    >
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <COREBody type="p2" strong={true} marginBottom={false}>
          {dateLabel}
        </COREBody>

        {tooltipPoints.map((pt, index) => (
          <React.Fragment key={`${pt.name}-${index}`}>
            <Row gutter={[4, 4]} align="middle" wrap={false}>
              <Col
                flex="none"
                style={{ display: "flex", alignItems: "center" }}
              >
                <TraceIcon variant={pt.variant} color={pt.color} />
              </Col>
              <Col flex="auto" style={{ minWidth: 0 }}>
                <COREBody
                  type="p3"
                  marginBottom={false}
                  strong={true}
                  testID={generateTestId("admin", "tooltip-trace-name")}
                  ellipsis
                >
                  {pt.name}
                </COREBody>
              </Col>
              <Col flex="24px"></Col>
              <Col flex="none" style={{ whiteSpace: "nowrap" }}>
                <COREBody type="p3" marginBottom={false}>
                  {formatTooltipValue(pt.value, pt.unit)}
                </COREBody>
              </Col>
            </Row>
          </React.Fragment>
        ))}
      </Space>
    </div>
  );
};

/**
 * Module-level stable reference for `customTooltip` prop.
 * Using this avoids creating a new arrow function on every render,
 * which would otherwise invalidate memoised dependencies inside COREChart.
 *
 * Usage: `customTooltip={renderTimeSeriesChartTooltip}`
 */
export const renderTimeSeriesChartTooltip = (
  state: COREChartTooltipState
): React.ReactNode => <CORETimeSeriesChartTooltip {...state} />;
